import axios, { AxiosResponse, HttpStatusCode, toFormData } from 'axios';
import { useApi } from '@/src/composables/useApi';
import httpClient from '@/src/lib/api/http/client';
import {
  TAddressInput,
  TBpjsInput,
  TEKTPInput,
  TEmergencyContactInput,
} from '@/src/modules/user/data/schemas/input';
import { RoutesApi } from '@/src/shared/constants/route';
import { withQuery } from '@/src/shared/utils/common';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import {
  mapAddresstoAddressCreateRequest,
  mapEmergencyContactCreateRequest,
  mapTorajaDeleteRequest,
  mapUpdateBpjsRequest,
  mapUpdateEKTPRequest,
  mapUpdateMainAddressRequest,
} from '../data/mappers';
import {
  SAddressInputResponse,
  SSimpleResponse,
  SUploadKtpResponse,
  SUserAndAddressResponse,
  TAddressInputResponse,
  TSimpleResponse,
  TUploadKtpResponse,
  TUserAndAddressResponse,
} from '../data/schemas/response';
import { GetUserAndAddressFailedError, UpdateAddressError } from '../errors';
import { MAddress } from '../models/address';

interface IAddressInputParams {
  input: TAddressInput;
  id: number | null;
  userId: number | undefined;
  individualId: number | undefined;
}

interface IEmergencyContactInputParams {
  input: TEmergencyContactInput;
  id: number | null;
  userId: number | undefined;
  individualId: number | undefined;
}
interface ISimpleDeleteParams {
  id: number;
  userId: number | undefined;
  model: string;
}

interface IUpdateMainAddressParams {
  address: MAddress;
  userId: number | undefined;
  individualId: number | undefined;
}

interface IBpjsInputParams {
  input: TBpjsInput;
  userId: number | undefined;
  individualId: number | undefined;
}

interface IEKTPInputParams {
  input: TEKTPInput;
}

export const userService = {
  // Should have been returning application model, but keep it returning
  // schema for now, until new auth system is implemented
  getUserAndAddress: async () => {
    try {
      const response: AxiosResponse<TUserAndAddressResponse> =
        await httpClient.healthcareIntelligence.get(
          '/subakun/get-user-address-data'
        );

      return safeParseZodObject<TUserAndAddressResponse>(
        SUserAndAddressResponse,
        response.data
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new GetUserAndAddressFailedError();
      }

      throw error;
    }
  },

  createAddress: async ({
    input,
    id,
    userId,
    individualId,
  }: IAddressInputParams) => {
    const request = mapAddresstoAddressCreateRequest(input, id, individualId);
    const api = useApi();
    const routesApi = RoutesApi();
    const response = await api.healthcareIntelligence.post<any>(
      withQuery(
        api.buildUrl(
          routesApi.AUTHENTICATION.UPDATE_PROFILE,
          'healthcareIntelligence'
        ),
        { user_id: userId }
      ),
      request
    );
    if (response?.data.status === HttpStatusCode.Ok) {
      const responseData = safeParseZodObject<TAddressInputResponse>(
        SAddressInputResponse,
        response.data
      );
      return responseData.message;
    }
    throw new UpdateAddressError();
  },

  createEmergencyContact: async ({
    input,
    id,
    userId,
    individualId,
  }: IEmergencyContactInputParams) => {
    const request = mapEmergencyContactCreateRequest(input, id, individualId);
    const api = useApi();
    const routesApi = RoutesApi();
    const response = await api.healthcareIntelligence.post<any>(
      withQuery(
        api.buildUrl(
          routesApi.AUTHENTICATION.UPDATE_PROFILE,
          'healthcareIntelligence'
        ),
        { user_id: userId }
      ),
      request
    );
    if (response?.data.status === HttpStatusCode.Ok) {
      const responseData = safeParseZodObject<TAddressInputResponse>(
        SAddressInputResponse,
        response.data
      );
      return responseData.message;
    }
    throw new UpdateAddressError();
  },

  deleteProfileEntity: async ({ id, userId, model }: ISimpleDeleteParams) => {
    const request = mapTorajaDeleteRequest(id, model);
    const api = useApi();
    const routesApi = RoutesApi();
    const response = await api.healthcareIntelligence.post<any>(
      withQuery(
        api.buildUrl(
          routesApi.AUTHENTICATION.UPDATE_PROFILE,
          'healthcareIntelligence'
        ),
        { user_id: userId }
      ),
      request
    );
    if (response?.data.status === HttpStatusCode.Ok) {
      const responseData = safeParseZodObject<TAddressInputResponse>(
        SAddressInputResponse,
        response.data
      );
      return responseData.message;
    }
    throw new UpdateAddressError();
  },

  updateMainAddress: async ({
    address,
    userId,
    individualId,
  }: IUpdateMainAddressParams) => {
    const request = mapUpdateMainAddressRequest(address, individualId);
    const api = useApi();
    const routesApi = RoutesApi();
    const response = await api.healthcareIntelligence.post<any>(
      withQuery(
        api.buildUrl(
          routesApi.AUTHENTICATION.UPDATE_PROFILE,
          'healthcareIntelligence'
        ),
        { user_id: userId }
      ),
      request
    );
    if (response?.data.status === HttpStatusCode.Ok) {
      const responseData = safeParseZodObject<TAddressInputResponse>(
        SAddressInputResponse,
        response.data
      );
      return responseData.message;
    }
    throw new UpdateAddressError();
  },

  updateBpjs: async ({ input, userId, individualId }: IBpjsInputParams) => {
    const request = mapUpdateBpjsRequest(input, userId, individualId);
    const api = useApi();
    const routesApi = RoutesApi();
    const response = await api.healthcareIntelligence.post<any>(
      withQuery(
        api.buildUrl(routesApi.AUTHENTICATION.UPDATE_BIODATA, 'izmoAuth'),
        { user_id: userId }
      ),
      request
    );
    if (response?.meta.status === HttpStatusCode.Ok) {
      return null;
    }
    throw new UpdateAddressError();
  },

  updateEKTP: async ({ input }: IEKTPInputParams) => {
    const request = mapUpdateEKTPRequest(input);
    const formData = toFormData(request);
    try {
      const response: AxiosResponse<TSimpleResponse> =
        await httpClient.healthcareIntelligence.post(
          '/subakun/post-create-ktp?unchanged=True',
          formData
        );
      return safeParseZodObject<TSimpleResponse>(
        SSimpleResponse,
        response.data
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new GetUserAndAddressFailedError();
      }
      throw error;
    }
  },

  uploadEKTP: async (file: File | string) => {
    const formData = new FormData();
    let fileToUpload: File;

    if (typeof file === 'string') {
      const res = await fetch(file);
      if (!res.ok) {
        throw new Error('Failed to fetch image from URL');
      }
      const blob = await res.blob();
      fileToUpload = new File([blob], 'ktp.jpg', {
        type: blob.type || 'image/jpeg',
      });
    } else {
      fileToUpload = file;
    }
    formData.append('file_ktp', fileToUpload);
    try {
      const response: AxiosResponse<TUploadKtpResponse> =
        await httpClient.healthcareIntelligence.post(
          '/subakun/post-create-image-ktp',
          formData
        );
      return safeParseZodObject<TUploadKtpResponse>(
        SUploadKtpResponse,
        response.data
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new GetUserAndAddressFailedError();
      }
      throw error;
    }
  },
};
